import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Loader2 } from 'lucide-react';

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`status-badge status-${String(status).replace(/\s+/g, '_')}`}>
      {status}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  count,
  actions,
}: {
  title: string;
  description?: string;
  count?: number;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div className="page-header-main">
        <h1 className="page-header-title">
          {title}
          {typeof count === 'number' ? <span className="badge">{count}</span> : null}
        </h1>
        {description ? <p className="page-header-description">{description}</p> : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </div>
  );
}

export function Breadcrumbs({ items }: { items: Array<{ label: string; to?: string; onClick?: () => void }> }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className="breadcrumb-item">
            {i > 0 ? <span className="breadcrumb-sep" aria-hidden>/</span> : null}
            {isLast || (!item.to && !item.onClick) ? (
              <span aria-current={isLast ? 'page' : undefined}>{item.label}</span>
            ) : (
              <button
                type="button"
                className="breadcrumb-link"
                onClick={() => {
                  if (item.onClick) item.onClick();
                }}
              >
                {item.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

export function Card({ children, title }: { children: ReactNode; title?: ReactNode }) {
  return (
    <div className="card">
      {title ? <div className="card-title">{title}</div> : null}
      {children}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="table-state state-panel" role="status" aria-live="polite">
      <Loader2 className="state-icon spin" size={28} aria-hidden />
      <strong className="state-title">{label}</strong>
    </div>
  );
}

export function ErrorState({
  description,
  onRetry,
  title = 'Something went wrong',
}: {
  description: string;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <div className="table-state state-panel error-state" role="alert">
      <AlertCircle className="state-icon" size={28} aria-hidden />
      <strong className="state-title">{title}</strong>
      <p className="muted state-desc">{description}</p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="table-state state-panel">
      <Inbox className="state-icon" size={28} aria-hidden />
      <strong className="state-title">{title}</strong>
      {description ? <p className="muted state-desc">{description}</p> : null}
      {action ? <div className="state-action">{action}</div> : null}
    </div>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={t.id === active}
          className={`tab ${t.id === active ? 'active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function ExportMenu({ onExport }: { onExport: (type: 'xlsx' | 'csv' | 'pdf') => void }) {
  return (
    <div className="export-menu">
      <select
        className="form-control form-control-sm"
        aria-label="Export"
        defaultValue=""
        onChange={(e) => {
          const v = e.target.value as 'xlsx' | 'csv' | 'pdf' | '';
          if (v) {
            onExport(v);
            e.target.value = '';
          }
        }}
      >
        <option value="" disabled>
          Export
        </option>
        <option value="xlsx">Excel</option>
        <option value="csv">CSV</option>
        <option value="pdf">PDF</option>
      </select>
    </div>
  );
}
